import { useShallow } from 'zustand/react/shallow';
import { startTransition, useCallback, useEffect } from 'react';
import cn from 'clsx';
import { type WithTestId } from '@pkgs/components/types/types';
import { useShopFilterRemovalMessage } from '@pkgs/components/hooks/useShopFilterRemovalMessage';
import { useLocationFilterInitialLocation } from '@pkgs/components/hooks/useLocationFilterInitialLocation';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import ArrowLeftIcon from '@pkgs/components/assets/icons/small/Arrow-2-Left.svg';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import AiStar from '@pkgs/components/assets/icons/small/AiStar.svg';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { MobileAutoComplete } from '../AutoComplete/MobileAutoComplete';
import { type AutoSuggestOption } from '../AutoComplete/AutoComplete';
import { MobileAiPromptInput } from '../AiLocationFilter/MobileAiLocationFilter';

import styles from './LocationFilterModalTabs.module.css';
import locationFilterStyles from './LocationFilter.module.css';

export type LocationFilterProps = WithTestId<{
  className?: string;
}>;

export const LocationFilterModalTabs = (props: LocationFilterProps) => {
  const { t } = useAppContext();
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const searchMode = useSearchStore((state) => state.searchMode);
  const modalSearchMode = useSearchStore((state) => state.modalSearchMode);
  const isLipaPage = useIsLipaPage();

  const { setPlaceIds, setPlaceName, setSearchMode, setModalSearchMode } = useSearchStore(
    useShallow((state) => ({
      setPlaceIds: state.setPlaceIds,
      setPlaceName: state.setPlaceName,
      setSearchMode: state.setSearchMode,
      setModalSearchMode: state.setModalSearchMode,
    })),
  );
  const { removeShopFilterWithUndoMessage } = useShopFilterRemovalMessage();

  useEffect(() => {
    // set the modal search mode to the global search mode on mount
    setModalSearchMode(searchMode);
  }, [searchMode, setModalSearchMode]);

  const handleSelection = useCallback(
    (selectedLocation: AutoSuggestOption) => {
      setIsMobileModalOpen(false);
      startTransition(() => {
        if (isLipaPage) {
          removeShopFilterWithUndoMessage();
        }
        setPlaceIds([selectedLocation.id]);
        setPlaceName(selectedLocation.title);
        setSearchMode(SearchMode.CLASSIC);
      });
    },
    [isLipaPage, removeShopFilterWithUndoMessage, setIsMobileModalOpen, setPlaceName, setSearchMode, setPlaceIds],
  );

  const classicSearchInputText = useLocationFilterInitialLocation({ visibleSearchMode: modalSearchMode });

  return (
    <>
      <div className={styles.searchModeTabs}>
        <div className={styles.backButtonWrapper}>
          <IconButton
            className={locationFilterStyles.backButton}
            size="xsmall"
            shape="square"
            icon={<ArrowLeftIcon />}
            onClick={() => setIsMobileModalOpen(false)}
            label={t('search.common.close')}
            variant="tertiary"
          />
        </div>

        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cn(styles.modeTab, styles.classicSearchModeTab)}
            data-selected={modalSearchMode === SearchMode.CLASSIC}
            onClick={() => setModalSearchMode(SearchMode.CLASSIC)}
          >
            <Overline className={styles.classicSearchOverline} text={t('search.aiSearch.classicMode.title')} />
          </button>
          <button
            type="button"
            className={cn(styles.modeTab, styles.aiSearchModeTab)}
            data-selected={modalSearchMode === SearchMode.AI}
            onClick={() => setModalSearchMode(SearchMode.AI)}
          >
            {modalSearchMode === SearchMode.AI ? <AiStarGradient /> : <AiStar />}
            <Overline className={styles.aiSearchOverline} text={t('search.aiSearch.aiMode.title')} />
          </button>
        </div>
      </div>

      {modalSearchMode === SearchMode.AI ? (
        <MobileAiPromptInput />
      ) : (
        <div className={styles.mobileAutoComplete}>
          <MobileAutoComplete handleSelection={handleSelection} initialLocation={classicSearchInputText} locationFilterProps={props} />
        </div>
      )}
    </>
  );
};
